import { Link } from 'react-router-dom'
import { Mail, Phone, MapPin } from 'lucide-react'
import { logos } from '@/assets/images'
import { WHATSAPP_URL } from '@/constants/whatsapp'
import { useCookieConsentContext } from '@/context/CookieConsentContext'
import { useTheme } from '@/context/ThemeContext'

type FooterLink = { name: string; href: string; external?: boolean }

const linkClass =
  'sp-muted transition-colors hover:text-[var(--sp-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--sp-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--sp-surface-2)]'

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const { openPreferences } = useCookieConsentContext()
  const { theme } = useTheme()
  const logoSrc = theme === 'dark' ? logos.logoWhite : logos.logo

  const footerLinks: {
    produto: FooterLink[]
    empresa: FooterLink[]
    suporte: FooterLink[]
    legal: FooterLink[]
  } = {
    produto: [
      { name: 'App do Aluno', href: '/apps#aluno' },
      { name: 'App da Escola', href: '/apps#escola' },
      { name: 'App do Professor', href: '/apps#professor' },
      { name: 'Funcionalidades', href: '/solucoes' },
    ],
    empresa: [
      { name: 'Sobre Nós', href: '/sobre' },
      { name: 'Contato', href: '/contato' },
    ],
    suporte: [
      { name: 'Fale conosco', href: '/contato' },
      { name: 'Agendar demonstração', href: WHATSAPP_URL, external: true },
    ],
    legal: [
      { name: 'Termos de Uso', href: '/termos' },
      { name: 'Política de Privacidade', href: '/privacidade' },
      { name: 'LGPD', href: '/privacidade' },
    ],
  }

  const renderLink = (link: FooterLink) =>
    link.external ? (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
      >
        {link.name}
      </a>
    ) : (
      <Link to={link.href} className={linkClass}>
        {link.name}
      </Link>
    )

  return (
    <footer
      style={{
        background: 'var(--sp-surface-2)',
        borderTop: '1px solid var(--sp-border-2)',
        color: 'var(--sp-text)',
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center mb-4">
              <img src={logoSrc} alt="SwimmingPool" className="h-8 w-auto" />
            </div>
            <p className="sp-muted mb-6 max-w-md text-[15px] leading-relaxed">
              Solução completa para escolas de natação. Automatize recebimentos, gerencie alunos e
              acompanhe o desempenho com nossos apps especializados.
            </p>

            <div className="space-y-3">
              <div className="flex items-center space-x-3 sp-muted text-sm">
                <Phone className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>(38) 99872-7085</span>
              </div>
              <div className="flex items-center space-x-3 sp-muted text-sm">
                <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>contato@swimmingpool.com.br</span>
              </div>
              <div className="flex items-center space-x-3 sp-muted text-sm">
                <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span>Montes Claros, MG - Brasil</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-lg font-semibold" style={{ color: 'var(--sp-text)' }}>
              Produto
            </h3>
            <ul className="space-y-2">
              {footerLinks.produto.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-lg font-semibold" style={{ color: 'var(--sp-text)' }}>
              Empresa
            </h3>
            <ul className="space-y-2">
              {footerLinks.empresa.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-lg font-semibold" style={{ color: 'var(--sp-text)' }}>
              Suporte
            </h3>
            <ul className="space-y-2">
              {footerLinks.suporte.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-lg font-semibold" style={{ color: 'var(--sp-text)' }}>
              Legal
            </h3>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={openPreferences}
                  className={`${linkClass} text-left`}
                >
                  Preferências de cookies
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div
          className="mt-12 pt-8"
          style={{ borderTop: '1px solid var(--sp-border-2)' }}
        >
          <div className="flex flex-col items-center justify-between md:flex-row">
            <p className="text-sm sp-muted">
              © {currentYear} SwimmingPool. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
